import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { HiCheck, HiX, HiClock, HiInformationCircle } from 'react-icons/hi';

const STATUS_CONFIG = {
  active: {
    light: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dark: 'dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-500/30',
    icon: HiCheck,
    label: 'Active'
  },
  inactive: {
    light: 'bg-gray-50 text-gray-700 border-gray-200',
    dark: 'dark:bg-gray-500/20 dark:text-gray-300 dark:border-gray-500/30',
    icon: HiX,
    label: 'Inactive'
  },
  approved: {
    light: 'bg-green-50 text-green-700 border-green-200',
    dark: 'dark:bg-green-500/20 dark:text-green-300 dark:border-green-500/30',
    icon: HiCheck,
    label: 'Approved'
  },
  selected: {
    light: 'bg-green-50 text-green-700 border-green-200',
    dark: 'dark:bg-green-500/20 dark:text-green-300 dark:border-green-500/30',
    icon: HiCheck,
    label: 'Selected'
  },
  rejected: {
    light: 'bg-red-50 text-red-700 border-red-200',
    dark: 'dark:bg-red-500/20 dark:text-red-300 dark:border-red-500/30',
    icon: HiX,
    label: 'Rejected'
  },
  'not-selected': {
    light: 'bg-red-50 text-red-700 border-red-200',
    dark: 'dark:bg-red-500/20 dark:text-red-300 dark:border-red-500/30',
    icon: HiX,
    label: 'Not Selected'
  },
  pending: {
    light: 'bg-amber-50 text-amber-700 border-amber-200',
    dark: 'dark:bg-amber-500/20 dark:text-amber-300 dark:border-amber-500/30',
    icon: HiClock,
    label: 'Pending'
  },
  info: {
    light: 'bg-blue-50 text-blue-700 border-blue-200',
    dark: 'dark:bg-blue-500/20 dark:text-blue-300 dark:border-blue-500/30',
    icon: HiInformationCircle,
    label: 'Info'
  }
};

const SIZE_CLASSES = {
  sm: 'px-2.5 py-1 text-xs',
  md: 'px-3 py-1.5 text-sm',
  lg: 'px-4 py-2 text-base'
};

const ICON_SIZES = {
  sm: 12,
  md: 14,
  lg: 16
};

const StatusBadge = ({
  status,
  label,
  size = 'md',
  showIcon = false,
  variant = 'solid',
  className,
  onClick,
  ...rest
}) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.info;
  const Icon = config.icon;
  const displayLabel = label || config.label;

  const badgeClasses = classNames(
    // Base styles
    'inline-flex items-center gap-1.5 font-semibold rounded-full border transition-all duration-200',

    // Size
    SIZE_CLASSES[size],

    // Color scheme
    variant === 'solid' && [config.light, config.dark],
    variant === 'subtle' && [
      config.light.replace('50', '100/50'),
      config.dark
    ],
    variant === 'outline' && [
      'bg-transparent',
      config.light.replace('bg-', 'border-').replace('-50', '-300'),
      config.dark.replace('bg-', 'border-')
    ],

    // Interactive
    onClick && 'cursor-pointer hover:shadow-md hover:scale-105 active:scale-95',

    // Custom classes
    className
  );

  const iconSize = ICON_SIZES[size];

  return (
    <span
      className={badgeClasses}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      {...rest}
    >
      {showIcon && <Icon size={iconSize} className="flex-shrink-0" />}
      <span className="leading-none">{displayLabel}</span>
    </span>
  );
};

StatusBadge.propTypes = {
  /**
   * Status type - determines color and icon
   */
  status: PropTypes.oneOf([
    'active',
    'inactive',
    'approved',
    'selected',
    'rejected',
    'not-selected',
    'pending',
    'info'
  ]).isRequired,

  /**
   * Custom label (overrides default status label)
   */
  label: PropTypes.string,

  /**
   * Badge size
   */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),

  /**
   * Show status icon
   */
  showIcon: PropTypes.bool,

  /**
   * Visual variant
   */
  variant: PropTypes.oneOf(['solid', 'subtle', 'outline']),

  /**
   * Additional CSS classes
   */
  className: PropTypes.string,

  /**
   * Click handler (makes badge interactive)
   */
  onClick: PropTypes.func
};

export default StatusBadge;
